import React, { useRef, useEffect, useLayoutEffect, useState, useMemo, useCallback } from "react";
import { Box } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import {
	DndContext,
	DragOverlay,
	PointerSensor,
	useSensor,
	useSensors,
	DragStartEvent,
	DragOverEvent,
	DragEndEvent,
	type Announcements,
} from "@dnd-kit/core";
import { Seat, SeatStudent } from "./Seat";
import { SeatDragOverlay } from "./SeatDragOverlay";
import { resolveDisplaySlot, seatIndexFromDndId as parseSeatDndId } from "./seatDnd";
import { Slot, SEAT_GRID_GAP_PX, SEAT_GRID_PADDING_X_PX, columnsFromAvailableWidth, tileScaleFromCardWidth } from "./seatLayout";
import { useSeatSelection } from "./useSeatSelection";
import { showsTabIconsFromScale, tileSizeFromScale } from "@/stores/v2/classroomLayoutStore";
interface SeatGridProps {
	slots: Slot[];
	columns: number;
	rows: number;
	cardWidth: number;
	selectionEnabled: boolean;
	/** Checkboxes stay visible but inert, e.g. while an arrangement is unsaved. */
	selectionDisabled?: boolean;
	onViewportColumns: (columns: number) => void;
	// Whether the rendered tiles include the per-student action toolbar row. Monitor-only
	// classes render action-less tiles, so empty/loading placeholders must be shorter to match.
	tilesHaveActionRow: boolean;
	studentsById: Record<string, SeatStudent>;
	cardsByStudent: Record<string, JSX.Element[]>;
	selectedStudents: string[];
	claimedQoriaIds: Set<string>;
	/** Students claimed by this class whose claim can be released from the seat corner. */
	releasableQoriaIds: Set<string>;
	activeId: number | null;
	overId: number | null;
	pendingMoveIndex: number | null;
	onToggleSelect: (id: string) => void;
	onReleaseClaim?: (id: string) => void;
	onDragStart: (index: number) => void;
	onDragOver: (index: number | null) => void;
	onDragEnd: (from: number, to: number) => void;
	onDragCancel: () => void;
	onPickUpSeat: (index: number) => void;
	onCancelMove: () => void;
	onPlaceSeat: (from: number, to: number) => void;
	/** When false, seats without cards show Class not started instead of connecting (CLASS-993). */
	isClassActive?: boolean;
	pinnedStudents?: SeatStudent[];
}

function nextSeatIndex(current: number, key: string, columns: number, rows: number, total: number): number {
	const row = Math.floor(current / columns);
	const col = current % columns;
	let nextRow = row;
	let nextCol = col;
	switch (key) {
		case "ArrowLeft":
			nextCol = Math.max(0, col - 1);
			break;
		case "ArrowRight":
			nextCol = Math.min(columns - 1, col + 1);
			break;
		case "ArrowUp":
			nextRow = Math.max(0, row - 1);
			break;
		case "ArrowDown":
			nextRow = Math.min(rows - 1, row + 1);
			break;
		default:
			return current;
	}
	return Math.min(nextRow * columns + nextCol, total - 1);
}

export function SeatGrid(props: SeatGridProps): JSX.Element {
	const {
		slots,
		columns,
		rows,
		cardWidth,
		onViewportColumns,
		selectionEnabled,
		selectionDisabled = false,
		tilesHaveActionRow,
		studentsById,
		cardsByStudent,
		selectedStudents,
		claimedQoriaIds,
		releasableQoriaIds,
		activeId,
		overId,
		pendingMoveIndex,
		onToggleSelect,
		onReleaseClaim,
		onDragStart,
		onDragOver,
		onDragEnd,
		onDragCancel,
		onPickUpSeat,
		onCancelMove,
		onPlaceSeat,
		isClassActive = false,
		pinnedStudents = [],
	} = props;
	const { t } = useTranslation();
	const tileScale = tileScaleFromCardWidth(cardWidth);
	const tileSize = tileSizeFromScale(tileScale);
	const tileScaleClassNames = clsx({
		StudentTile_grid_med: tileSize === "lg",
		StudentTile_grid_small: tileSize === "sm",
		StudentTile_grid_noTabIcons: !showsTabIconsFromScale(tileScale),
	});
	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
	const containerRef = useRef<HTMLDivElement>(null);
	const marqueeRef = useRef<HTMLDivElement>(null);
	const panCleanup = useRef<(() => void) | null>(null);
	const [focusedSeatIndex, setFocusedSeatIndex] = useState(0);
	const [liveMessage, setLiveMessage] = useState("");
	useEffect(() => () => panCleanup.current?.(), []);

	const getStudentName = useCallback(
		(index: number): string | null => {
			const id = slots[index];
			if (!id) return null;
			const student = studentsById[id];
			return student ? `${student.firstName} ${student.lastName}` : null;
		},
		[slots, studentsById]
	);

	const handleActivateSeat = useCallback(
		(index: number) => {
			if (pendingMoveIndex === null) {
				if (!slots[index]) return;
				const name = getStudentName(index);
				if (!name) return;
				onPickUpSeat(index);
				setLiveMessage(t("seatPlan.pickedUp", { name, n: index + 1 }));
				return;
			}

			if (pendingMoveIndex === index) {
				onCancelMove();
				setLiveMessage(t("seatPlan.moveCancelled"));
				return;
			}

			const from = pendingMoveIndex;
			const fromName = getStudentName(from);
			const toName = getStudentName(index);
			const toHadStudent = !!slots[index];

			onPlaceSeat(from, index);

			if (fromName && toHadStudent && toName) {
				setLiveMessage(t("seatPlan.swapped", { nameA: fromName, nameB: toName }));
			} else if (fromName) {
				setLiveMessage(t("seatPlan.placed", { name: fromName, n: index + 1 }));
			}
		},
		[pendingMoveIndex, slots, getStudentName, onPickUpSeat, onCancelMove, onPlaceSeat, t]
	);

	const handleGridKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (pendingMoveIndex === null && !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) && e.key !== "Escape") return;

			if (e.key === "Escape" && pendingMoveIndex !== null) {
				e.preventDefault();
				onCancelMove();
				setLiveMessage(t("seatPlan.moveCancelled"));
				return;
			}

			if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) return;

			e.preventDefault();
			const next = nextSeatIndex(focusedSeatIndex, e.key, columns, rows, slots.length);
			setFocusedSeatIndex(next);
			const target = containerRef.current?.querySelector<HTMLElement>(`[data-testid="seatplan-seat-${next}"]`);
			target?.focus();
		},
		[pendingMoveIndex, columns, rows, slots.length, onCancelMove, focusedSeatIndex, t]
	);

	const dndAnnouncements = useMemo<Announcements>(
		() => ({
			onDragStart: ({ active }) => {
				const idx = parseSeatDndId(active.id, slots.length);
				const name = getStudentName(idx);
				return name ? t("seatPlan.dragStart", { name, n: idx + 1 }) : undefined;
			},
			onDragOver: ({ active, over }) => {
				if (!over) return undefined;
				const activeIdx = parseSeatDndId(active.id, slots.length);
				const overIdx = parseSeatDndId(over.id, slots.length);
				const name = getStudentName(activeIdx);
				return name ? t("seatPlan.dragOver", { name, n: overIdx + 1 }) : undefined;
			},
			onDragEnd: ({ active, over }) => {
				if (!over) return undefined;
				const activeIdx = parseSeatDndId(active.id, slots.length);
				const overIdx = parseSeatDndId(over.id, slots.length);
				const name = getStudentName(activeIdx);
				return name ? t("seatPlan.dragEnd", { name, n: overIdx + 1 }) : undefined;
			},
			onDragCancel: ({ active }) => {
				const idx = parseSeatDndId(active.id, slots.length);
				const name = getStudentName(idx);
				return name ? t("seatPlan.dragCancel", { name }) : undefined;
			},
		}),
		[getStudentName, t, slots.length]
	);

	// Grow the scroll container to fill the viewport below the toolbar so the marquee and
	// middle-click pan work across the whole page area, not only over the tile rows.
	const [fillHeight, setFillHeight] = useState<number | undefined>(undefined);
	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const measure = () => setFillHeight(Math.max(0, window.innerHeight - el.getBoundingClientRect().top));
		measure();
		window.addEventListener("resize", measure);
		// Recompute when anything above the grid (toolbar, banners) changes height.
		const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		for (let sibling = el.parentElement?.previousElementSibling ?? null; sibling; sibling = sibling.previousElementSibling) {
			observer?.observe(sibling);
		}
		return () => {
			window.removeEventListener("resize", measure);
			observer?.disconnect();
		};
	}, []);

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const measure = () => {
			const available = el.clientWidth - SEAT_GRID_PADDING_X_PX * 2;
			onViewportColumns(columnsFromAvailableWidth(available, cardWidth, SEAT_GRID_GAP_PX));
		};
		measure();
		const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
		ro?.observe(el);
		window.addEventListener("resize", measure);
		return () => {
			ro?.disconnect();
			window.removeEventListener("resize", measure);
		};
	}, [cardWidth, onViewportColumns]);

	// Marquee and Cmd/Ctrl+A update the same store selection that controls each checkbox.
	// Disable every selection path while an arrangement is waiting to be saved or discarded.
	const selection = useSeatSelection(containerRef, marqueeRef, selectionEnabled && !selectionDisabled);

	const handleDragStart = (e: DragStartEvent) => {
		onDragStart(parseSeatDndId(e.active.id, slots.length));
	};
	const handleDragOver = (e: DragOverEvent) => onDragOver(e.over ? parseSeatDndId(e.over.id, slots.length) : null);
	const handleDragEnd = (e: DragEndEvent) => {
		if (e.over) onDragEnd(parseSeatDndId(e.active.id, slots.length), parseSeatDndId(e.over.id, slots.length));
		else onDragCancel();
	};
	const handleDragCancel = () => onDragCancel();

	const handleMiddleMousePan = (e: React.MouseEvent) => {
		if (e.button !== 1 || !containerRef.current) return;
		// Suppress the browser's native middle-click autoscroll so our manual pan takes over.
		e.preventDefault();
		const el = containerRef.current;
		const startX = e.clientX;
		const startY = e.clientY;
		const startLeft = el.scrollLeft;
		const startTop = el.scrollTop;
		const previousCursor = el.style.cursor;
		el.style.cursor = "grabbing";
		const onMove = (ev: MouseEvent) => {
			el.scrollLeft = startLeft - (ev.clientX - startX);
			el.scrollTop = startTop - (ev.clientY - startY);
		};
		const onUp = () => {
			window.removeEventListener("mousemove", onMove);
			window.removeEventListener("mouseup", onUp);
			el.style.cursor = previousCursor;
			panCleanup.current = null;
		};
		window.addEventListener("mousemove", onMove);
		window.addEventListener("mouseup", onUp);
		panCleanup.current = onUp;
	};

	const overlayStudentId = activeId !== null ? (slots[activeId] as string | null) : null;
	const overlayStudent = overlayStudentId ? (studentsById[overlayStudentId] ?? null) : null;
	const overlayCard = overlayStudentId ? cardsByStudent[overlayStudentId]?.[0] : undefined;

	return (
		<>
			<Box
				ref={containerRef}
				overflow="auto"
				overflowX="hidden"
				// The top padding also clears the pin bubbles, which overhang the first row's tiles.
				p="20px 24px 24px 24px"
				height={fillHeight ? `${fillHeight}px` : undefined}
				data-testid="seatplan-grid"
				userSelect="none"
				onMouseDown={handleMiddleMousePan}
				onPointerDown={selection.onPointerDown}
				onKeyDown={handleGridKeyDown}
				sx={{
					"&::-webkit-scrollbar": { display: "none" },
					scrollbarWidth: "none",
					msOverflowStyle: "none",
				}}
			>
				<Box
					aria-live="polite"
					aria-atomic="true"
					data-testid="seatplan-live-region"
					sx={{
						position: "absolute",
						width: "1px",
						height: "1px",
						padding: 0,
						margin: "-1px",
						overflow: "hidden",
						clip: "rect(0, 0, 0, 0)",
						whiteSpace: "nowrap",
						border: 0,
					}}
				>
					{liveMessage}
				</Box>
				<Box
					ref={marqueeRef}
					data-testid="seatplan-marquee"
					position="fixed"
					bg="rgba(0,117,219,0.1)"
					border="2px solid #0075db"
					pointerEvents="none"
					zIndex={998}
					style={{ display: "none" }}
				/>
				<DndContext
					sensors={sensors}
					onDragStart={handleDragStart}
					onDragOver={handleDragOver}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
					accessibility={{
						announcements: dndAnnouncements,
						screenReaderInstructions: {
							draggable: t("seatPlan.moveInstructions"),
						},
					}}
				>
					{pinnedStudents.length > 0 && (
						<Box
							data-testid="seatplan-pinned-section"
							display="grid"
							gridTemplateColumns={`repeat(${columns}, ${cardWidth}px)`}
							gap={`${SEAT_GRID_GAP_PX}px`}
							width="100%"
							mt="10px"
							mb="0.75rem"
						>
							{pinnedStudents.map((student, i) => (
								<Seat
									key={student.qoriaUserId}
									index={10000 + i}
									student={student}
									cards={cardsByStudent[student.qoriaUserId]}
									cardWidth={cardWidth}
									selected={selectedStudents.includes(student.qoriaUserId)}
									draggable={false}
									droppable={false}
									selectable={selectionEnabled && !claimedQoriaIds.has(student.qoriaUserId)}
									selectionDisabled={selectionDisabled}
									releasable={!!onReleaseClaim && releasableQoriaIds.has(student.qoriaUserId)}
									onToggleSelect={onToggleSelect}
									onReleaseClaim={onReleaseClaim}
									hasActionRow={tilesHaveActionRow}
									isClassActive={isClassActive}
								/>
							))}
						</Box>
					)}
					{pinnedStudents.length > 0 && (
						<Box
							data-testid="seatplan-pinned-divider"
							aria-hidden="true"
							width="100%"
							height="3px"
							borderRadius="full"
							bg="rgba(255, 255, 255, 0.5)"
							mb="1.75rem"
						/>
					)}
					<Box
						className={tileScaleClassNames}
						display="grid"
						gridTemplateColumns={`repeat(${columns}, ${cardWidth}px)`}
						gridAutoRows="max-content"
						gap={`${SEAT_GRID_GAP_PX}px`}
						width="100%"
					>
						{slots.map((_, index) => {
							const display = resolveDisplaySlot(slots, index, activeId);
							const slotId = slots[index];
							return (
								<Seat
									key={index}
									index={index}
									student={display.id ? (studentsById[display.id] ?? null) : null}
									cards={display.id ? cardsByStudent[display.id] : undefined}
									cardWidth={cardWidth}
									selected={!!slotId && selectedStudents.includes(slotId as string)}
									selectable={selectionEnabled && !!slotId && !claimedQoriaIds.has(slotId as string)}
									selectionDisabled={selectionDisabled}
									releasable={!!onReleaseClaim && !!slotId && releasableQoriaIds.has(slotId as string)}
									onToggleSelect={onToggleSelect}
									onReleaseClaim={onReleaseClaim}
									isOver={overId === index}
									isOriginHole={display.isOriginHole}
									pendingMove={pendingMoveIndex === index}
									tabIndex={index === focusedSeatIndex ? 0 : -1}
									onActivate={handleActivateSeat}
									hasActionRow={tilesHaveActionRow}
									isClassActive={isClassActive}
								/>
							);
						})}
						{activeId !== null &&
							Array.from({ length: columns }, (_, col) => (
								<Seat
									key={`ghost-${col}`}
									index={col}
									student={null}
									cardWidth={cardWidth}
									selected={false}
									draggable={false}
									selectable={false}
									onToggleSelect={onToggleSelect}
									isOver={overId === slots.length + col}
									ghost
									hasActionRow={tilesHaveActionRow}
									isClassActive={isClassActive}
								/>
							))}
					</Box>
					<DragOverlay dropAnimation={null}>
						{overlayStudent ? (
							<SeatDragOverlay
								student={overlayStudent}
								cardWidth={cardWidth}
								card={overlayCard}
								tileScaleClassNames={tileScaleClassNames}
							/>
						) : null}
					</DragOverlay>
				</DndContext>
			</Box>
		</>
	);
}
